<script>
import { isEqual, get } from 'lodash-es'
import { TRACKING_DRIVING_MARKER_ANIMATION } from '@/config/tackingConfig'

export default {
    name: 'HereMarker',
    inject: ['map', 'ui', 'group'],
    props: {
        lat: {
            type: Number,
            required: true
        },
        lng: {
            type: Number,
            required: true
        },
        icon: {
            type: Object,
            required: false
        },
        iconComponent: {
            type: [Object, Boolean],
            default: false,
            required: false,
        },
        zIndex: {
            type: Number,
            required: false,
            default: 1
        },
        options: {
            required: false,
            default: undefined
        },
        isOnClick: {
            type: Boolean,
            required: false,
            default: false
        },
        onClick: {
            type: Function,
            required: false
        },
        onDoubleClick: {
            type: Function,
            required: false
        },
        onMouseOver: {
            type: Function,
            required: false
        },
        onMouseLeave: {
            type: Function,
            required: false
        },
        animatedMove: {
            type: Boolean,
            required: false
        },
        autoTap: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data () {
        return {
            marker: null
        }
    },
    computed: {
        latLng () {
            return `${this.lat}|${this.lng}`
        }
    },
    watch: {
        latLng (newVal, oldVal) {
            const [oldLat, oldLng] = oldVal.split('|')
            const [newLat, newLng] = newVal.split('|')

            if ((null != newLat && null != newLng) && (oldLat !== newLat && oldLng !== newLng)) {
                if (this.animatedMove) {
                    this.updateMarkerLocationAnimated(newLat, newLng, oldLat, oldLng)
                } else {
                    this.updateMarkerLocation()
                }
            }
        },
        icon (newVal, oldVal) {
            if (!isEqual(newVal, oldVal)) {
                this.updateMarkerIcon()
            }
        },
        options: {
            handler: function (newVal, oldVal) {
                if (!isEqual(get(newVal, 'data', true), get(oldVal, 'data', true))) {
                    this.updateMarkerData()
                }
            },
            deep: true
        },
        map: {
            handler: function (newVal) {
                if (null != newVal) {
                    this.createMarker()
                }
            },
            deep: true
        }

    },
    mounted () {
        this.$nextTick(() => {
            this.createMarker()
        })
    },
    updated () {
        if (null == this.marker) {
            this.createMarker()
        }
    },
    beforeDestroy () {
        this.removeMarker(false)
    },
    methods: {

        /**
         * Construct the marker options based on props.
         */
        getOptions () {
            let hIcon;
            if (null != this.icon) {
                hIcon = new H.map.Icon(this.icon.bitmap, this.icon.options)
                return { ...this.options, icon: hIcon }
            } else if (this.iconComponent) {
                hIcon = this.iconComponent
                return { ...this.options, icon: hIcon }
            }

            return this.options
        },

        /**
         * Update the marker's data located in the options prop.
         */
        updateMarkerData () {
            if (null == this.marker) return
            this.marker.setData(get(this.options, 'data', {}))
        },

        /**
         * Use the current Marker lat and lng values to update the marker position.
         */
        updateMarkerLocation () {
            if (null == this.marker) return

            this.marker.setGeometry({ lat: this.lat, lng: this.lng })
        },

        updateMarkerLocationAnimated (newLat, newLng, oldLat, oldLng) {
            if (null == this.marker) return

            let marker = this.marker
            this.ease(
                new H.geo.Point(oldLat, oldLng),
                new H.geo.Point(newLat, newLng),
                TRACKING_DRIVING_MARKER_ANIMATION,
                function (coord) {
                    marker.setGeometry(coord)
                }
            )
        },

        /**
         * Use the current Marker icon to update the marker icon.
         */
        updateMarkerIcon () {
            const { bitmap, options } = this.icon

            if (null == this.marker) return

            if (null != this.icon) {
                this.marker.setIcon(new H.map.Icon(bitmap, options))
            }
        },

        /**
         * Remove the associated Marker from the map
         */
        removeMarker (shouldUpdateState = true) {
            if (null == this.marker) return

            if (null == this.map() && null == this.group()) return

            const target = null != this.group() ? this.group() : this.map()
            if (null != target) {
                target.removeObject(this.marker)
            }

            if (shouldUpdateState) {
                this.marker = null
            }
        },

        /**
         * Add event handlers to the target Marker
         */
        addEventHandlers (marker) {
            /**
             * We need to emit to $parent, as we are emiting from Slot
             */
            marker.addEventListener('tap', (evt) => this.$parent.$emit('on-click', evt))
            if (!this.isOnClick) {
                marker.addEventListener('dbltap', (evt) => this.$parent.$emit('on-double-click', evt))
                marker.addEventListener('pointerenter', (evt) => this.$parent.$emit('on-mouse-over', evt))
                marker.addEventListener('pointerleave', (evt) => this.$parent.$emit('on-mouse-leave', evt))
            }
        },

        /**
         * Generate the marker based on component props.
         */
        instantiateMarker () {
            const options = this.getOptions()

            const marker = new H.map.Marker({ lat: this.lat, lng: this.lng }, options)

            marker.setZIndex(this.zIndex)

            this.addEventHandlers(marker)

            return marker
        },

        /**
         * Create a new Marker and assign it to the map.
         */
        createMarker () {
            if (null == this.map() || null != this.marker) return null

            const marker = this.instantiateMarker()

            this.marker = marker

            const target = null != this.group() ? this.group() : this.map()

            target.addObject(marker)

            // this.map().addObject(marker)

            if (this.autoTap) {
                marker.dispatchEvent('tap')
            }

            return marker
        },

        ease (
            startCoord,
            endCoord,
            durationMs,
            onStep = console.log,
            onComplete = function () {}
        ) {
            let raf = window.requestAnimationFrame || function (f) { window.setTimeout(f, 16) }
            let stepCount = durationMs / 16
            let valueIncrementLat = (endCoord.lat - startCoord.lat) / stepCount
            let valueIncrementLng = (endCoord.lng - startCoord.lng) / stepCount
            let sinValueIncrement = Math.PI / stepCount
            let currentValueLat = startCoord.lat
            let currentValueLng = startCoord.lng
            let currentSinValue = 0

            function step () {
                currentSinValue += sinValueIncrement
                currentValueLat += valueIncrementLat * (Math.sin(currentSinValue) ** 2) * 2
                currentValueLng += valueIncrementLng * (Math.sin(currentSinValue) ** 2) * 2

                if (currentSinValue < Math.PI) {
                    onStep({ lat: currentValueLat, lng: currentValueLng })
                    raf(step)
                } else {
                    onStep(endCoord)
                    onComplete()
                }
            }

            raf(step)
        }
    },
    render () {
        return null
    }
}
</script>
